import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { apiClient, handleApiError } from '@/config/api';
import { MyClassesResponse } from '@/types/api';

/**
 * "My Classes" — the signed-in staff member's classes for one day of the
 * current week, with rotations and rosters (GET /classes/mine). The backend
 * scopes it from the JWT; the client only sends the date (omitted = today).
 *
 * ⚠️ Registered in BOTH store/index.ts and store/index.web.ts — a slice missing
 * from the web store is `undefined` on the PWA (blank-screen crash).
 */
interface MyClassesState {
  data: MyClassesResponse | null;
  /** The requested day (yyyy-MM-dd), or null for "today". */
  date: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: MyClassesState = {
  data: null,
  date: null,
  isLoading: false,
  error: null,
};

export const getMyClasses = createAsyncThunk(
  'myClasses/fetch',
  async (date: string | null, { rejectWithValue }) => {
    try {
      const query = date ? `?date=${encodeURIComponent(date)}` : '';
      return await apiClient.get<MyClassesResponse>(`/classes/mine${query}`);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

const myClassesSlice = createSlice({
  name: 'myClasses',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // The requested day is selected synchronously, so the last tap wins.
      .addCase(getMyClasses.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        state.date = action.meta.arg;
      })
      // Drop out-of-order responses from rapid day-arrow taps.
      .addCase(getMyClasses.fulfilled, (state, action) => {
        if (action.meta.arg !== state.date) return;
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(getMyClasses.rejected, (state, action) => {
        if (action.meta.arg !== state.date) return;
        state.isLoading = false;
        state.error = (action.payload as string) ?? 'Could not load your classes';
      });
  },
});

export default myClassesSlice.reducer;
